<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { RouterLink } from 'vue-router';

import { eqsApi } from '@/api/eqs';
import DelegateDialog from '@/components/DelegateDialog.vue';
import RecordHistory from '@/components/RecordHistory.vue';
import { useWorkCalendar } from '@/composables/useWorkCalendar';
import { notify } from '@/services/notice';
import type { PlanTask } from '@/types/domain';
import { errorMessage, formatResponsibility } from '@/utils/format';
import { buildProgressIndex, progressTitle } from '@/utils/plan-progress';

// 必须给 canWrite 显式默认值：Vue 对 boolean 类型的 prop 缺省是 false 而不是
// undefined，不写 withDefaults 的话「没传」会被当成「只读」，写入控件全部消失。
const props = withDefaults(defineProps<{
  task: PlanTask | null;
  projectId: string | number;
  tasks: PlanTask[];
  canWrite?: boolean;
  saving?: boolean;
}>(), {
  canWrite: true,
  saving: false,
});

const emit = defineEmits<{
  close: [];
  save: [task: PlanTask, payload: { start: string; end: string; status: string | null }];
  /** 依赖改动后让上层重新拉一次计划 */
  dependenciesChanged: [];
}>();

// 从节点发起派活（0903）：派出去的任务挂在这个节点上，任务跟踪表里看得见
const delegating = ref(false);

/** 与后端 assertPlanNodeTransition 一致；approved/rejected 只能由审批链产生 */
const STATUS_TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  '': ['pending', 'in_progress', 'completed', 'not_applicable'],
  pending: ['in_progress', 'completed', 'blocked', 'not_applicable'],
  in_progress: ['completed', 'blocked', 'not_applicable'],
  blocked: ['in_progress', 'completed', 'not_applicable'],
  completed: ['in_progress'],
  approved: ['in_progress'],
  rejected: ['in_progress', 'not_applicable'],
  not_applicable: ['pending', 'in_progress'],
};

const STATUS_LABEL: Readonly<Record<string, string>> = {
  pending: '待处理', in_progress: '进行中', blocked: '受阻', completed: '已完成',
  approved: '已批准', rejected: '已退回', not_applicable: '不适用',
};

interface Dependency {
  id: number;
  predecessorId: number;
  predecessorName: string;
  successorId: number;
  successorName: string;
  lagDays: number;
  violated: boolean;
  earliestStart: string | null;
}

const draftStart = ref('');
const draftEnd = ref('');
const workCalendar = useWorkCalendar();
const startRestHint = computed(() => workCalendar.hint(draftStart.value, '计划开始日'));
const endRestHint = computed(() => workCalendar.hint(draftEnd.value, '计划完成日'));
const draftStatus = ref('');
const dateError = ref('');

const dependencies = ref<Dependency[]>([]);
const depLoading = ref(false);
const depPredecessor = ref('');
const depLag = ref('0');
const depSaving = ref(false);

const readOnly = computed(() => props.canWrite === false);

/*
  完成度（0930）：和甘特条、阶段工作表同一个算法。这里只显示——
  报进度在「阶段工作」的抽屉里（那边有完成度、实际完成、状态三格联动），
  甘特这边管排期，两处都能改同一格反而容易各改各的。
*/
const progress = computed(() => {
  if (!props.task) return null;
  return buildProgressIndex(props.tasks).get(String(props.task.id)) ?? null;
});

function statusLabel(status: string | null | undefined): string {
  if (!status) return '待处理';
  return STATUS_LABEL[status] ?? status;
}

/*
  甘特这边不给「改为已完成」（0930 Tony：「工作台不能直接点击完成…偷懒直接完成了」）。
  标完成要有证据——挂了交付物的每份都得有文件，没挂的要写一句完成说明——
  那些都在「阶段工作」的任务抽屉里看得到、填得了；这里只留一个去那边的入口。
*/
const statusOptions = computed(() => {
  const current = props.task?.status ?? '';
  return (STATUS_TRANSITIONS[current] ?? STATUS_TRANSITIONS[''])
    .filter((value) => value !== 'completed' || props.task?.nodeType === 'phase')
    .map((value) => ({ value, label: STATUS_LABEL[value] ?? value }));
});
/** 去「阶段工作」抽屉标完成：带 focus=complete，抽屉会直接拉到 100% 并摆出还差什么 */
const completeLink = computed(() => {
  const task = props.task;
  if (!task || task.nodeType === 'phase' || ['completed', 'approved', 'not_applicable'].includes(task.status ?? '')
    || task.actualEnd) return null;
  return `/projects/${props.projectId}/activities?nodeId=${encodeURIComponent(String(task.id))}&edit=1&focus=complete`;
});

/** 本节点的前置与后置，分开列——方向不同，含义完全不同 */
const predecessors = computed(() =>
  dependencies.value.filter((item) => item.successorId === Number(props.task?.id)));
const successors = computed(() =>
  dependencies.value.filter((item) => item.predecessorId === Number(props.task?.id)));

/** 可选的前置：排掉自己和已经连上的，避免建重复或自环 */
const candidates = computed(() => {
  const taken = new Set(predecessors.value.map((item) => item.predecessorId));
  return props.tasks
    .filter((item) => String(item.id) !== String(props.task?.id) && !taken.has(Number(item.id)))
    .slice(0, 400);
});

function reset(): void {
  draftStart.value = props.task?.planStart?.slice(0, 10) ?? '';
  draftEnd.value = props.task?.planEnd?.slice(0, 10) ?? '';
  // 留空 = 只改日期。打开抽屉本身不该产生状态变更
  draftStatus.value = '';
  dateError.value = '';
  depPredecessor.value = '';
  depLag.value = '0';
}

async function loadDependencies(): Promise<void> {
  if (!props.task) return;
  depLoading.value = true;
  try {
    const payload = await eqsApi.planDependencies(props.projectId);
    dependencies.value = (payload.dependencies ?? []) as unknown as Dependency[];
  } catch {
    dependencies.value = [];
  } finally {
    depLoading.value = false;
  }
}

function submit(): void {
  if (!props.task) return;
  if (!draftStart.value || !draftEnd.value) {
    dateError.value = '请同时填写计划开始和计划完成日期。';
    return;
  }
  if (draftEnd.value < draftStart.value) {
    dateError.value = '计划完成日期不能早于计划开始日期。';
    return;
  }
  dateError.value = '';
  emit('save', props.task, {
    start: draftStart.value,
    end: draftEnd.value,
    status: draftStatus.value || null,
  });
}

async function addDependency(): Promise<void> {
  if (!props.task || !depPredecessor.value) return;
  depSaving.value = true;
  try {
    const payload = await eqsApi.createPlanDependency(props.projectId, {
      predecessorId: Number(depPredecessor.value),
      successorId: Number(props.task.id),
      lagDays: Number(depLag.value) || 0,
    });
    dependencies.value = (payload.dependencies ?? []) as unknown as Dependency[];
    depPredecessor.value = '';
    depLag.value = '0';
    notify('success', '前置已设置', '前置推迟时，这条会收到顺延建议。');
    emit('dependenciesChanged');
  } catch (caught) {
    notify('error', '前置设置失败', errorMessage(caught));
  } finally {
    depSaving.value = false;
  }
}

async function removeDependency(id: number): Promise<void> {
  depSaving.value = true;
  try {
    const payload = await eqsApi.removePlanDependency(props.projectId, id);
    dependencies.value = (payload.dependencies ?? []) as unknown as Dependency[];
    emit('dependenciesChanged');
  } catch (caught) {
    notify('error', '删除失败', errorMessage(caught));
  } finally {
    depSaving.value = false;
  }
}

function onKey(event: KeyboardEvent): void {
  if (event.key === 'Escape' && props.task && !props.saving) emit('close');
}

watch(() => props.task?.id, () => {
  reset();
  if (props.task) void loadDependencies();
}, { immediate: true });

onMounted(() => window.addEventListener('keydown', onKey));
onUnmounted(() => window.removeEventListener('keydown', onKey));
</script>

<template>
  <div v-if="task" class="node-drawer-mask" @click.self="!saving && emit('close')">
    <aside class="node-drawer" role="dialog" aria-modal="true" :aria-label="`编辑 ${task.name}`">
      <header>
        <div>
          <span>PLAN NODE</span>
          <h3>{{ task.name }}</h3>
          <p>
            {{ formatResponsibility(task.ownerName || task.ownerRole, '责任人未分配') }}
            <template v-if="task.helpers?.length"> · 协办 {{ task.helpers.map((helper) => helper.name).join('、') }}</template>
            · 当前 {{ statusLabel(task.status) }}
            <template v-if="progress && progress.value !== null">
              · <span class="nd-progress" :title="progressTitle(progress)">完成度 {{ progress.value }}%</span>
            </template>
          </p>
        </div>
        <div class="nd-head-actions">
          <!-- 派活（0903）：节点里要人去办的事派出去，挂在这个节点上；不改节点本身 -->
          <button class="inline-button" type="button" :disabled="saving" @click="delegating = true">派活</button>
          <button class="inline-button" type="button" :disabled="saving" @click="emit('close')">关闭</button>
        </div>
      </header>
      <DelegateDialog
        v-if="delegating"
        :project-id="Number(projectId)"
        :reference="{ type: 'plan-node', id: task.id, label: `计划节点 ${task.name}` }"
        @close="delegating = false"
      />

      <form class="nd-form" @submit.prevent="submit">
        <fieldset :disabled="readOnly || saving">
          <legend>计划日期</legend>
          <div class="nd-dates">
            <label><span>计划开始</span><input v-model="draftStart" type="date"></label>
            <label><span>计划完成</span><input v-model="draftEnd" type="date"></label>
          </div>
          <!-- 1006 工作日历：日期落在周末 / 法定假日上要说一声，17% 的计划完成日原来落在周六周日 -->
          <p v-if="startRestHint" class="nd-rest-hint">{{ startRestHint }}</p>
          <p v-if="endRestHint" class="nd-rest-hint">{{ endRestHint }}</p>
          <label class="nd-status">
            <span>状态</span>
            <select v-model="draftStatus">
              <option value="">保持{{ statusLabel(task.status) }}</option>
              <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                改为{{ option.label }}
              </option>
            </select>
            <small>留空表示只改日期，不动状态。</small>
            <small v-if="completeLink" class="nd-complete">
              标完成要看交付物文件或写完成说明，在阶段工作里办。
              <RouterLink class="inline-button" :to="completeLink">去标完成 →</RouterLink>
            </small>
          </label>
          <p v-if="dateError" class="nd-error">{{ dateError }}</p>
        </fieldset>

        <div v-if="!readOnly" class="nd-actions">
          <button class="button button-primary" type="submit" :disabled="saving">
            {{ saving ? '保存中…' : '保存' }}
          </button>
          <button class="button" type="button" :disabled="saving" @click="emit('close')">取消</button>
        </div>
        <p v-else class="nd-readonly">当前账号为只读，不能修改计划。</p>
      </form>

      <section class="nd-deps">
        <h4>前后置依赖</h4>
        <p class="nd-hint">
          设了前置之后，前置一推迟，这条会收到顺延建议。只做「完成 → 开始」一种关系。
        </p>

        <p v-if="depLoading" class="nd-muted">正在读取…</p>
        <template v-else>
          <div class="nd-dep-group">
            <b>前置（要先做完的）</b>
            <ul v-if="predecessors.length">
              <li v-for="item in predecessors" :key="item.id" :class="{ violated: item.violated }">
                <span>{{ item.predecessorName }}</span>
                <em v-if="item.lagDays">间隔 {{ item.lagDays }} 天</em>
                <em v-if="item.violated" class="nd-violated">
                  冲突：最早只能 {{ item.earliestStart }} 开始
                </em>
                <!-- 不叫「移除」：同一个词在评审面板里指「把人从名单去掉」，这里指「解除两个节点的先后关系」 -->
                <button
                  v-if="!readOnly" type="button" :disabled="depSaving"
                  :title="`解除与「${item.predecessorName}」的先后关系；两个节点都还在，只是不再相互约束日期`"
                  @click="removeDependency(item.id)"
                >解除依赖</button>
              </li>
            </ul>
            <small v-else class="nd-muted">没有前置。</small>
          </div>

          <div class="nd-dep-group">
            <b>后置（等这条做完的）</b>
            <ul v-if="successors.length">
              <li v-for="item in successors" :key="item.id">
                <span>{{ item.successorName }}</span>
                <em v-if="item.lagDays">间隔 {{ item.lagDays }} 天</em>
              </li>
            </ul>
            <small v-else class="nd-muted">没有后置。</small>
          </div>

          <div v-if="!readOnly" class="nd-dep-add">
            <label>
              <span>添加前置</span>
              <select v-model="depPredecessor">
                <option value="">选择一个必须先完成的节点</option>
                <option v-for="item in candidates" :key="item.id" :value="String(item.id)">
                  {{ item.name }}
                </option>
              </select>
            </label>
            <label class="nd-lag">
              <span>间隔天数</span>
              <input v-model="depLag" type="number" step="1">
              <small>负数表示允许提前搭接</small>
            </label>
            <button
              class="button"
              type="button"
              :disabled="!depPredecessor || depSaving"
              @click="addDependency"
            >{{ depSaving ? '保存中…' : '添加' }}</button>
          </div>
        </template>
      </section>

      <RecordHistory
        entity-type="plan-node"
        :entity-id="task.id"
        hint="这条节点的日期和状态改过几次。"
      />
    </aside>
  </div>
</template>

<style scoped>
.node-drawer-mask {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  justify-content: flex-end;
  background: rgba(16, 39, 56, .35);
}

/* 抽屉而不是居中弹窗：左边的甘特要一直看得见，
   否则改一条日期就失去了「它相对别人在哪」的判断依据。 */
.node-drawer {
  width: min(520px, 100%);
  height: 100%;
  overflow-y: auto;
  padding: 18px 20px 28px;
  background: var(--white);
  box-shadow: -8px 0 28px rgba(16, 39, 56, .18);
}

.node-drawer > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.nd-head-actions { display: flex; gap: 6px; flex-shrink: 0; }
.node-drawer header span { color: var(--muted); font: 700 12px var(--display); letter-spacing: .08em; }
.node-drawer header h3 { margin: 3px 0 4px; font-size: 17px; line-height: 1.4; }
.node-drawer header p { margin: 0; color: var(--muted); font-size: 12px; }
.node-drawer header p .nd-progress { color: var(--blue); font: 700 12px var(--body); letter-spacing: 0; }

.nd-form { margin-top: 16px; }
.nd-form fieldset { margin: 0; padding: 12px 14px; border: 1px solid var(--line); }
.nd-form legend { padding: 0 6px; color: var(--muted); font-size: 12px; font-weight: 700; }
.nd-dates { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.nd-rest-hint { margin: 6px 0 0; font-size: 12px; line-height: 1.5; color: #b06a12; }
.nd-form label { display: grid; gap: 4px; }
.nd-form label > span { color: var(--muted); font-size: 12px; }
.nd-form input, .nd-form select { height: 36px; padding: 0 9px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; }
.nd-status { margin-top: 10px; }
.nd-status small, .nd-lag small { color: var(--muted); font-size: 12px; }
.nd-status .nd-complete { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; margin-top: 4px; }
.nd-error { margin: 8px 0 0; color: var(--risk); font-size: 12px; }
.nd-actions { display: flex; gap: 8px; margin-top: 12px; }
.nd-readonly { margin: 12px 0 0; color: var(--muted); font-size: 12px; }

.nd-deps { margin-top: 18px; padding-top: 14px; border-top: 1px solid var(--line); }
.nd-deps h4 { margin: 0 0 4px; font-size: 14px; }
.nd-hint { margin: 0 0 10px; color: var(--muted); font-size: 12px; line-height: 1.7; }
.nd-muted { color: var(--muted); font-size: 12px; }
.nd-dep-group { margin-bottom: 10px; }
.nd-dep-group b { display: block; margin-bottom: 4px; font-size: 12px; }
.nd-dep-group ul { display: grid; gap: 4px; margin: 0; padding: 0; list-style: none; }
.nd-dep-group li { display: flex; gap: 8px; align-items: baseline; flex-wrap: wrap; padding: 5px 8px; background: var(--paper); font-size: 12px; }
.nd-dep-group li.violated { border-left: 3px solid var(--risk); }
.nd-dep-group li em { color: var(--muted); font-style: normal; font-size: 12px; }
.nd-violated { color: var(--risk) !important; font-weight: 700; }
.nd-dep-group li button { margin-left: auto; min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--risk); border-color: rgba(196,63,56,.4); }
.nd-dep-group li button:hover { border-color: var(--risk); background: rgba(196,63,56,.07); }

.nd-dep-add { display: grid; gap: 8px; margin-top: 10px; padding: 10px; border: 1px dashed var(--line); }
.nd-dep-add label { display: grid; gap: 4px; }
.nd-dep-add label > span { color: var(--muted); font-size: 12px; }
.nd-dep-add select, .nd-dep-add input { height: 34px; padding: 0 9px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 13px; }
</style>
